package mapsearch

import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.LatLon
import apiclient.geoobjects.MarkerColor
import apiclient.geoobjects.MarkerIcon
import apiclient.geoobjects.MarkerShape
import apiclient.mapsearch.MapFeature
import apiclient.mapsearch.MapMarker
import apiclient.mapsearch.MapMarkerTileSearchResults
import apiclient.mapsearch.MarkerCluster
import apiclient.mapsearch.SearchSpec
import apiclient.mapsearch.UserMarker
import apiclient.util.isNotNullOrEmpty
import apiclient.validations.parseEnumValue
import com.jillesvangurp.geo.tiles.Tile
import com.jillesvangurp.geo.tiles.Tile.Companion.coordinateToTile
import kotlin.math.min
import maplibreGL.Marker
import model.MapState
import utils.md5
import utils.parseInstant

fun GeoObjectDetails.updateFromMapMarker(marker: MapMarker): GeoObjectDetails {
    return this.copy(
        title = marker.title,
        geometry = marker.geometry,
        latLon = marker.position,
        updatedAt = marker.updatedAt.toString(),
        iconCategory = parseEnumValue<MarkerIcon>(marker.icon) ?: this.iconCategory,
        color = parseEnumValue<MarkerColor>(marker.color) ?: this.color,
        shape = parseEnumValue<MarkerShape>(marker.shape) ?: this.shape,
        keywords = marker.keywords,
        attachments = marker.content,
    )
}

fun GeoObjectDetails.mapMarker() =
    MapMarker(
        id = id,
        ownerId = ownerId,
        title = title,
        geometry = geometry ?: error("no geometry"),
        updatedAt = updatedAt.parseInstant() ?: error("invalid updatedAt"),
        icon = iconCategory?.name,
        color = color?.name,
        keywords = keywords,
    )

val MapFeature.contentHash: String
    get() = when (this) {
        is MapMarker -> {
            listOf(id, updatedAt)
        }

        is MarkerCluster -> {
            listOf(tilePath) + markers?.map { it.contentHash }.orEmpty()
        }

        is UserMarker -> {
            listOf(userId, updatedAt)
        }

        else -> {
            error("")
        }
    }.md5


data class MapMarkerTileSearchResultsHolder(
    val mapMarkerTileSearchResults: MapMarkerTileSearchResults,
    val hasOverrides: Boolean = false
) {
    val contentHash: String by lazy {
        (
            listOf(mapMarkerTileSearchResults.tilePath) +
                mapMarkerTileSearchResults.clusters.map { it.contentHash }
            ).md5
    }

    override fun equals(other: Any?): Boolean {
        return if (other == null) {
            false
        } else {
            if (other is MapMarkerTileSearchResultsHolder) {
                mapMarkerTileSearchResults.tilePath == other.mapMarkerTileSearchResults.tilePath && contentHash == other.contentHash
            } else {
                false
            }
        }
    }

    override fun hashCode(): Int {
        return contentHash.hashCode()
    }
}

fun LatLon.tile(zoom: Int) = coordinateToTile(lat, lon, zoom)

fun MapState.tiles(): Pair<Int, List<Tile>> {
    if (zoom.toInt() == 0) {
        return 0 to listOf(Tile(0, 0, 0))
    }
    if (tl == null || br == null) {
        return 0 to emptyList()
    }

    val zoomLevel = min(zoom.toInt(), 22)

    val topLeftTile = tl.tile(zoomLevel)
    val bottomRightTile = br.tile(zoomLevel)

    val tiles = mutableListOf<Tile>()

    // Iterate through the tile grid from top-left to bottom-right
    for (x in topLeftTile.x..bottomRightTile.x) {
        for (y in topLeftTile.y..bottomRightTile.y) {
            tiles.add(Tile(x, y, zoomLevel))
        }
    }
    return zoomLevel to tiles
}

/**
 * Used to determine if SearchSpec is containing filters set by GlobalSearch (true) or not,
 * then it is MapSearch (false)
 */
fun SearchSpec.isActive(): Boolean {
    return !query.isNullOrBlank()
        || tags.isNotNullOrEmpty()
        || orTags.isNotNullOrEmpty()
        || objectTypes?.size == 1
}

val Marker.elementId get() = this.asDynamic()._element?.id as? String
